import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import DescriptionIcon from '@material-ui/icons/Description'
import HistoryIcon from '@material-ui/icons/History'
import LinkIcon from '@material-ui/icons/Link'
import RefreshIcon from '@material-ui/icons/Refresh'
import { Link as RouterLink } from 'react-router-dom'

const WORKFLOWS = {
  timer: {
    label: 'Irrigation Timer',
    apiBase: '/api/mobileapp/irrigation/timer-voice',
    supportsUrl: true,
    defaultEntityType: 'controller',
    transcriptPlaceholder: 'Station one runs Monday Wednesday Friday for 25 minutes at 8 AM.',
    examples: [
      {
        label: 'Simple Timer',
        transcript: 'Station one runs Monday Wednesday and Friday for 25 minutes at 8 AM.'
      },
      {
        label: 'Shared Times',
        transcript: 'Station number one runs for 25 minutes. Station number two runs for 15 minutes. They both start at 8 AM and 4 PM every day of the week.'
      }
    ]
  },
  stationOhms: {
    label: 'Station Ohms',
    apiBase: '/api/mobileapp/irrigation/station-ohms-voice',
    supportsUrl: false,
    defaultEntityType: 'controller',
    transcriptPlaceholder: 'Station 1 reads 27 ohms. Station 2 reads 14. Or: 42, 23, 24, 42.',
    examples: [
      {
        label: 'Explicit Ohms',
        transcript: 'Station one reads 27 ohms. Station two reads 14. Station three reads 42.'
      },
      {
        label: 'Sequence Ohms',
        transcript: '42, 23, 24, 42'
      }
    ]
  },
  arbor: {
    label: 'Arbor Scope',
    apiBase: '/api/mobileapp/arbor/voice',
    supportsUrl: false,
    defaultEntityType: 'arbor_scope',
    transcriptPlaceholder: 'Tree one is a mesquite, 25 feet tall, 14 inch DBH, 20 foot canopy, medium density, raise canopy and remove deadwood.',
    examples: [
      {
        label: 'Single Trim',
        transcript: 'Tree one is a mesquite in the front yard. It is about 35 feet tall. Girth is 8 feet around. Canopy starts around 13 feet and spreads about 45 feet. Canopy is heavy. Customer wants canopy raised over the driveway and deadwood removed. There are service lines on the east side and a clear drop zone toward the street. Haul all debris.'
      },
      {
        label: 'Group Palms',
        transcript: 'Group back wall palms. Future plan remove these next year. Internal note customer wants a budget option. One-off reason storm cleanup only. Tree one is a queen palm 30 feet tall 12 inch DBH 10 foot canopy seed pods. Tree two is a date palm 25 feet tall 14 inch DBH 12 foot canopy.'
      },
      {
        label: 'Limited Access',
        transcript: 'Tree one is a ficus in the backyard by the pool, 25 feet tall, 18 inch DBH, 30 foot canopy, limited drop zone, no lift access, rigging needed, leave logs and chip brush.'
      },
      {
        label: 'Shared Objective',
        transcript: 'For this group raise canopy and remove deadwood. Group front yard mesquites. Tree one is a mesquite 25 feet tall 12 inch DBH 20 foot canopy. Tree two is a mesquite 22 feet tall 10 inch DBH 18 foot canopy. Tree three is a ficus 18 feet tall 9 inch DBH 15 foot canopy remove the tree.'
      },
      {
        label: 'Quantity Group',
        transcript: 'Three queen palms along the back wall all about 20 feet. Seed pods and haul all debris. Clear drop zone toward the alley.'
      },
      {
        label: 'Reset Scope',
        transcript: 'For this group raise canopy and remove deadwood. Tree one is a mesquite 25 feet tall 12 inch DBH 20 foot canopy. Next tree only remove the tree. Tree two is a ficus 18 feet tall 9 inch DBH 15 foot canopy. Back to normal trim. Tree three is an olive 16 feet tall 8 inch DBH 14 foot canopy.'
      }
    ]
  }
}

// The workbench is intentionally data-driven. These are the questions we want
// to answer with real recordings before each context is released to the field.
const CONTEXT_WORKBENCH = {
  generic: {
    label: 'Generic',
    processor: 'generic',
    endpoint: '/api/mobileapp/voice/parse',
    description: 'Unscoped notes, tasks, issues, and follow-ups from any screen.',
    contextFields: ['propertyId', 'jobSiteId', 'assetId', 'equipmentId'],
    seedContext: { propertyId: 'PROP-90101', jobSiteId: 'SITE-NORTH' },
    options: [
      { id: 'note', label: 'Note', expectedFields: ['title', 'description'], en: 'North gate was open during the morning visit.', es: 'La puerta norte estaba abierta durante la visita de la mañana.' },
      { id: 'task', label: 'Task', expectedFields: ['title', 'description', 'priority', 'dueDate'], en: 'Create a follow-up for the north property next Friday about the broken gate.', es: 'Crear un seguimiento para la propiedad norte el próximo viernes sobre la puerta dañada.' },
      { id: 'issue', label: 'Issue', expectedFields: ['title', 'description', 'priority'], en: 'Issue at the north property: the gate is broken and needs attention today.', es: 'Problema en la propiedad norte: la puerta está rota y necesita atención hoy.' },
      { id: 'follow_up', label: 'Follow-up', expectedFields: ['title', 'description', 'dueDate'], en: 'Remind me to call the customer about the broken gate next Tuesday.', es: 'Recuérdame llamar al cliente sobre la puerta dañada el próximo martes.' }
    ]
  },
  shop: {
    label: 'Shop',
    processor: 'shop',
    endpoint: '/api/mobileapp/shop/voice/parse',
    description: 'Equipment maintenance tickets, repairs, and vehicle inspection records.',
    contextFields: ['equipmentId', 'equipmentNumber', 'repairTicketId', 'assetId'],
    seedContext: { equipmentNumber: '14', equipmentId: 'eq_14' },
    options: [
      { id: 'create_maintenance_ticket', label: 'MTC ticket', expectedFields: ['equipmentId', 'description'], en: 'Create an MTC ticket for truck 14 for an oil change on Friday.', es: 'Crear una solicitud de mantenimiento para el camión 14, cambio de aceite el viernes.' },
      { id: 'create_repair', label: 'New repair', expectedFields: ['equipmentId', 'description', 'priority'], en: 'New repair for truck 14. Hydraulic leak, high priority, out of service, needs parts.', es: 'Nueva reparación para el camión 14. Fuga hidráulica, prioridad alta, fuera de servicio, necesita piezas.' },
      { id: 'record_vehicle_inspection', label: 'Vehicle inspection', expectedFields: ['equipmentId', 'currentMileage', 'nextServiceMileage'], en: 'Vehicle inspection for truck 14. Current mileage 124500. Oil changed, 5W-30, battery is 12.6 volts.', es: 'Inspección del vehículo 14. Kilometraje actual 124500. Cambio de aceite 5W-30 y batería a 12.6 voltios.' }
    ]
  },
  irrigation: {
    label: 'Irrigation',
    processor: 'irrigation',
    description: 'Controller inspections, timer programs, and station resistance readings.',
    contextFields: ['propertyId', 'jobSiteId', 'inspectionId', 'assetId', 'controllerId', 'programId', 'stationNo'],
    seedContext: { propertyId: 'PROP-90101', controllerId: 'CTRL-18', inspectionId: 'IRR-TEST-01' },
    options: [
      { id: 'inspection', label: 'Inspection', endpoint: '/api/mobileapp/irrigation/inspection-voice/parse', expectedFields: ['batteryVoltage', 'stationOhms', 'issues', 'actionItems'], en: 'Inspection for controller 18. Battery is 12.4 volts. Station one is 36 ohms. Station two is open. Repair the valve box.', es: 'Inspección del controlador 18. La batería marca 12.4 voltios. La estación uno marca 36 ohmios. La estación dos está abierta. Reparar la caja.' },
      { id: 'timer_program', label: 'Timer / program', endpoint: '/api/mobileapp/irrigation/timer-voice/parse', expectedFields: ['programDays', 'startTimes', 'stationRuntimes'], en: 'Station one runs Monday Wednesday Friday for 25 minutes at 8 AM and 4 PM.', es: 'La estación uno funciona lunes miércoles y viernes durante 25 minutos a las 8 de la mañana y 4 de la tarde.' },
      { id: 'station_ohms', label: 'Station ohms', endpoint: '/api/mobileapp/irrigation/station-ohms-voice/parse', expectedFields: ['stationReadings'], en: 'Station one reads 27 ohms. Station two reads 14. Station three reads 42.', es: 'La estación uno marca 27 ohmios. La estación dos marca 14. La estación tres marca 42.' }
    ]
  },
  herbicide: {
    label: 'Herbicide',
    processor: 'generic',
    endpoint: '/api/mobileapp/voice/parse',
    description: 'Treatment observations, application notes, safety issues, and return visits.',
    contextFields: ['propertyId', 'jobSiteId', 'workTicketId', 'assetId'],
    seedContext: { propertyId: 'PROP-90101', workTicketId: 'WT-HERB-01' },
    options: [
      { id: 'observation', label: 'Observation', expectedFields: ['description', 'target'], en: 'Observation: nutsedge is returning near the south entrance and needs a treatment review.', es: 'Observación: el coquillo está regresando cerca de la entrada sur y necesita revisión del tratamiento.' },
      { id: 'application_note', label: 'Application note', expectedFields: ['description', 'applicationStatus'], en: 'Application note: treated nutsedge near the south entrance; allow the area to dry before opening.', es: 'Nota de aplicación: tratamos coquillo cerca de la entrada sur; dejar secar el área antes de abrir.' },
      { id: 'issue', label: 'Issue', expectedFields: ['description', 'priority'], en: 'Herbicide issue: wind increased and there may be drift toward the playground. Stop and notify the supervisor.', es: 'Problema de herbicida: aumentó el viento y puede haber deriva hacia el parque. Detener y avisar al supervisor.' },
      { id: 'follow_up', label: 'Follow-up', expectedFields: ['description', 'dueDate'], en: 'Return to check the south entrance treatment next Thursday.', es: 'Regresar para revisar el tratamiento de la entrada sur el próximo jueves.' }
    ]
  },
  maintenance: {
    label: 'Maintenance',
    processor: 'generic',
    endpoint: '/api/mobileapp/voice/parse',
    description: 'Property service observations, corrective work requests, and customer follow-ups.',
    contextFields: ['propertyId', 'jobSiteId', 'workTicketId', 'routeId'],
    seedContext: { propertyId: 'PROP-90101', routeId: 'ROUTE-NORTH-12' },
    options: [
      { id: 'service_note', label: 'Service note', expectedFields: ['description'], en: 'Service note: the west dumpster enclosure was clean on today’s visit.', es: 'Nota de servicio: el área de contenedores oeste estaba limpia durante la visita de hoy.' },
      { id: 'property_issue', label: 'Property issue', expectedFields: ['description', 'priority'], en: 'Property issue at Desert Ridge: broken gate by the west dumpster, high priority.', es: 'Problema de propiedad en Desert Ridge: puerta rota junto al contenedor oeste, prioridad alta.' },
      { id: 'work_request', label: 'Work request', expectedFields: ['description', 'priority'], en: 'Work request: repair the broken gate at the north entrance tomorrow morning.', es: 'Solicitud de trabajo: reparar la puerta rota de la entrada norte mañana por la mañana.' },
      { id: 'follow_up', label: 'Follow-up', expectedFields: ['description', 'dueDate'], en: 'Follow up with the property manager after the gate repair is complete.', es: 'Dar seguimiento con el administrador después de completar la reparación de la puerta.' }
    ]
  },
  supervisor: {
    label: 'Supervisor',
    processor: 'generic',
    endpoint: '/api/mobileapp/voice/parse',
    description: 'Crew notes, coaching, safety observations, and route follow-ups.',
    contextFields: ['propertyId', 'jobSiteId', 'crewId', 'employeeId', 'routeId'],
    seedContext: { crewId: 'CREW-12', routeId: 'ROUTE-NORTH-12', employeeId: 'EMP-44' },
    options: [
      { id: 'crew_note', label: 'Crew note', expectedFields: ['description', 'crewMember'], en: 'Crew note for crew 12: the team finished the north route early and restocked the trailer.', es: 'Nota para la cuadrilla 12: el equipo terminó la ruta norte temprano y reabasteció el remolque.' },
      { id: 'safety_issue', label: 'Safety issue', expectedFields: ['description', 'priority'], en: 'Safety issue for crew 12: trailer lights are not working. High priority before the next route.', es: 'Problema de seguridad para la cuadrilla 12: no funcionan las luces del remolque. Prioridad alta antes de la próxima ruta.' },
      { id: 'coaching', label: 'Coaching', expectedFields: ['description', 'crewMember'], en: 'Coaching note for Luis: recognized careful equipment inspection and customer communication.', es: 'Nota de capacitación para Luis: reconocer la inspección cuidadosa del equipo y la comunicación con el cliente.' },
      { id: 'follow_up', label: 'Follow-up', expectedFields: ['description', 'dueDate'], en: 'Follow up with crew 12 after the trailer lights are repaired.', es: 'Dar seguimiento con la cuadrilla 12 después de reparar las luces del remolque.' }
    ]
  },
  sales: {
    label: 'Sales',
    processor: 'generic',
    endpoint: '/api/mobileapp/voice/parse',
    description: 'Lead conversations, opportunity updates, site walks, and next steps.',
    contextFields: ['propertyId', 'jobSiteId', 'leadId', 'opportunityId'],
    seedContext: { propertyId: 'PROP-90101', opportunityId: 'OPP-ACME-22' },
    options: [
      { id: 'lead_note', label: 'Lead note', expectedFields: ['description', 'contact'], en: 'Lead note: Maria from Acme HOA asked about tree trimming and irrigation repairs.', es: 'Nota del cliente potencial: María de Acme HOA preguntó sobre poda de árboles y reparaciones de riego.' },
      { id: 'opportunity_note', label: 'Opportunity note', expectedFields: ['description', 'nextStep'], en: 'Opportunity note for Acme HOA: send the tree proposal by next Tuesday and schedule a site walk.', es: 'Nota de oportunidad para Acme HOA: enviar la propuesta de árboles el próximo martes y programar una visita.' },
      { id: 'site_walk', label: 'Site walk', expectedFields: ['description'], en: 'Site walk: the west slope needs erosion control and the entry monument needs seasonal color.', es: 'Visita: la pendiente oeste necesita control de erosión y el monumento de entrada necesita color de temporada.' },
      { id: 'follow_up', label: 'Follow-up', expectedFields: ['description', 'dueDate'], en: 'Call Maria next Tuesday to confirm the proposal meeting.', es: 'Llamar a María el próximo martes para confirmar la reunión de la propuesta.' }
    ]
  },
  audit: {
    label: 'Audit',
    processor: 'generic',
    endpoint: '/api/mobileapp/voice/parse',
    description: 'Findings, punchlist items, enhancements, and customer notes linked to an audit.',
    contextFields: ['propertyId', 'jobSiteId', 'auditId', 'inspectionId'],
    seedContext: { propertyId: 'PROP-90101', auditId: 'AUDIT-2026-01', jobSiteId: 'SITE-NORTH' },
    options: [
      { id: 'issue', label: 'Issue', expectedFields: ['description', 'priority', 'location'], en: 'Audit issue at the north entrance: broken irrigation box, high priority.', es: 'Problema de auditoría en la entrada norte: caja de riego rota, prioridad alta.' },
      { id: 'punchlist', label: 'Punchlist', expectedFields: ['description', 'location'], en: 'Punchlist item at the north entrance: replace the broken irrigation box lid.', es: 'Elemento de lista en la entrada norte: reemplazar la tapa rota de la caja de riego.' },
      { id: 'enhancement', label: 'Enhancement', expectedFields: ['description', 'recommendation'], en: 'Enhancement opportunity: add drip irrigation to the new west planting bed.', es: 'Oportunidad de mejora: agregar riego por goteo al nuevo jardín oeste.' },
      { id: 'customer_note', label: 'Customer note', expectedFields: ['description'], en: 'Customer note: the manager wants service completed before the community event.', es: 'Nota del cliente: el administrador quiere el servicio terminado antes del evento comunitario.' }
    ]
  },
  arbor: {
    label: 'Arbor',
    processor: 'arbor',
    endpoint: '/api/mobileapp/arbor/voice/parse',
    description: 'Tree scope, measurements, condition, access, risk, disposal, and objectives.',
    contextFields: ['propertyId', 'jobSiteId', 'inspectionId', 'assetId'],
    seedContext: { propertyId: 'PROP-90101', jobSiteId: 'SITE-NORTH', inspectionId: 'ARBOR-SCOPE-01' },
    options: [
      { id: 'tree_scope', label: 'Tree scope', expectedFields: ['species', 'measurements', 'objectives', 'access', 'disposal'], en: 'Tree one is a 30-foot mesquite by the driveway. Raise canopy and remove deadwood. There are service lines on the east side; use the clear drop zone toward the street.', es: 'Árbol uno es un mezquite de 30 pies junto a la entrada. Levantar la copa y quitar madera muerta. Hay líneas de servicio al este; usar la zona de caída hacia la calle.' }
    ]
  }
}

const CONTEXT_ORDER = ['generic', 'shop', 'irrigation', 'herbicide', 'maintenance', 'supervisor', 'sales', 'audit', 'arbor']
const TABS = ['Summary', 'Tree Cards', 'Structured JSON', 'Transcript', 'Warnings', 'Files', 'Raw']
const FEEDBACK_CATEGORIES = [
  ['species', 'Species'],
  ['measurements', 'Size'],
  ['objectives', 'Objective'],
  ['risks', 'Risk'],
  ['access', 'Access'],
  ['disposal', 'Disposal'],
  ['notes', 'Notes']
]

const emptyFeedback = () => ({
  accepted: false,
  missed: {},
  wrong: {},
  reviewerNote: '',
  correctedFields: null
})

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    maxWidth: 1600,
    margin: '0 auto',
    backgroundColor: '#f3f5f7',
    minHeight: '100vh'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2)
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  panel: {
    padding: theme.spacing(2),
    height: '100%'
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1.5)
  },
  controlsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: theme.spacing(1)
  },
  actions: {
    display: 'grid',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5)
  },
  historyList: {
    display: 'grid',
    gap: theme.spacing(1),
    maxHeight: 680,
    overflow: 'auto',
    paddingRight: theme.spacing(0.5)
  },
  historyItem: {
    width: '100%',
    textAlign: 'left',
    justifyContent: 'flex-start',
    textTransform: 'none',
    padding: theme.spacing(1.25),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: '#fff'
  },
  selectedHistoryItem: {
    borderColor: theme.palette.primary.main,
    boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`
  },
  itemMeta: {
    display: 'flex',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap',
    marginTop: theme.spacing(0.75)
  },
  examples: {
    display: 'flex',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  cardsOutput: {
    padding: theme.spacing(2),
    minHeight: 560,
    maxHeight: 760,
    overflow: 'auto',
    backgroundColor: '#fff'
  },
  treeCards: {
    display: 'grid',
    gap: theme.spacing(1.5)
  },
  treeCard: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    backgroundColor: '#fff'
  },
  treeCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1)
  },
  cardFields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  fieldLabel: {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: 2
  },
  feedbackPanel: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    backgroundColor: '#f8fafc',
    marginBottom: theme.spacing(1.5)
  },
  feedbackRows: {
    display: 'grid',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  feedbackRow: {
    display: 'grid',
    gridTemplateColumns: '80px 1fr',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  output: {
    margin: 0,
    padding: theme.spacing(2),
    minHeight: 560,
    maxHeight: 760,
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'Menlo, Monaco, Consolas, "Liberation Mono", monospace',
    fontSize: 13,
    lineHeight: 1.5,
    backgroundColor: '#fff'
  },
  statusRow: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  field: {
    marginBottom: theme.spacing(1.5)
  },
  hiddenInput: {
    display: 'none'
  },
  fileName: {
    marginTop: theme.spacing(0.75),
    color: theme.palette.text.secondary
  },
  contextWorkbench: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderTop: '4px solid #2e7d32'
  },
  contextHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1.5)
  },
  contextPills: {
    display: 'flex',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1.5)
  },
  contextOptions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5)
  },
  contextOption: {
    minHeight: 74,
    textAlign: 'left',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    textTransform: 'none',
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: '#fff'
  },
  selectedContextOption: {
    borderColor: theme.palette.primary.main,
    boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
    backgroundColor: '#f1f8f2'
  },
  contextExpected: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap',
    marginTop: theme.spacing(0.75)
  },
  contextWorkbenchGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.2fr) minmax(280px, .8fr)',
    gap: theme.spacing(2),
    alignItems: 'start',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr'
    }
  },
  contextResult: {
    margin: 0,
    padding: theme.spacing(1.5),
    minHeight: 250,
    maxHeight: 480,
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'Menlo, Monaco, Consolas, "Liberation Mono", monospace',
    fontSize: 12,
    lineHeight: 1.45,
    backgroundColor: '#f8fafc',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4
  }
}))

const parseFromEntry = (entry) => entry?.payload?.data?.parse || entry?.data?.parse || {}
const recordingFromEntry = (entry) => entry?.payload?.data?.recording || entry?.data?.recording || {}
const structuredFromEntry = (entry) => parseFromEntry(entry)?.structuredData || {}
const feedbackFromEntry = (entry) => entry?.humanFeedback || entry?.payload?.data?.humanFeedback || entry?.data?.humanFeedback || null
const requestIdFromEntry = (entry) => entry?.requestId || entry?.payload?.requestId || entry?.payload?.data?.requestId || entry?.data?.requestId || null

const statusColor = (status) => {
  if (status === 'parsed') return 'primary'
  if (status === 'needs_review') return 'secondary'
  return 'default'
}

const formatDate = (value) => {
  if (!value) return 'Unknown time'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString()
}

const formatJson = (value) => JSON.stringify(value || null, null, 2)

const parseContextData = (value) => {
  try {
    const parsed = JSON.parse(value || '{}')
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') throw new Error('Context data must be a JSON object.')
    return parsed
  } catch (error) {
    throw new Error(`Context JSON is invalid: ${error.message}`)
  }
}

const contextResultData = (result) => result?.data?.data || result?.data || result || {}
const contextParseData = (result) => contextResultData(result)?.parse || contextResultData(result)

const getDisplayLabel = (entry) => {
  const recording = recordingFromEntry(entry)
  return entry?.label || recording.originalName || recording.sourceUrl || 'Voice audit run'
}

const getSnippet = (entry) => {
  const parse = parseFromEntry(entry)
  const err = entry?.error?.message || entry?.payload?.error
  const text = parse.transcript || parse.error || err || ''
  return text.length > 140 ? `${text.slice(0, 140)}...` : text
}

const outputForTab = (tab, entry) => {
  const parse = parseFromEntry(entry)
  const recording = recordingFromEntry(entry)
  if (!entry) return 'Select a run from history or run a new test.'
  if (tab === 'Summary') return parse.summary || parse.error || entry?.error?.message || formatJson(entry)
  if (tab === 'Structured JSON') return formatJson(parse.structuredData || parse)
  if (tab === 'Transcript') return parse.transcript || ''
  if (tab === 'Warnings') return formatJson(parse.warnings || [])
  if (tab === 'Files') return formatJson(recording)
  return formatJson(entry)
}

const valueText = (value, fallback = '-') => {
  if (value == null || value === '') return fallback
  if (Array.isArray(value)) return value.length ? value.join(', ') : fallback
  return String(value)
}

const IrrigationVoiceAuditPage = (props) => {
  const classes = useStyles()
  const [history, setHistory] = useState([])
  const [selected, setSelected] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState('')
  const [workflow, setWorkflow] = useState('timer')

  const [recordingUrl, setRecordingUrl] = useState('')
  const [transcript, setTranscript] = useState('')
  const [file, setFile] = useState(null)
  const [backend, setBackend] = useState('')
  const [model, setModel] = useState('')
  const [siteId, setSiteId] = useState('')
  const [entityType, setEntityType] = useState(WORKFLOWS.timer.defaultEntityType)
  const [entityId, setEntityId] = useState('')
  const [status, setStatus] = useState('')
  const [kind, setKind] = useState('')
  const [needsHumanReview, setNeedsHumanReview] = useState('')
  const [feedbackDraft, setFeedbackDraft] = useState(emptyFeedback())
  const [savingFeedback, setSavingFeedback] = useState(false)

  const [contextType, setContextType] = useState('generic')
  const [contextOption, setContextOption] = useState('note')
  const [contextLanguage, setContextLanguage] = useState('auto')
  const [contextTranscript, setContextTranscript] = useState(CONTEXT_WORKBENCH.generic.options[0].en)
  const [contextDataText, setContextDataText] = useState(formatJson(CONTEXT_WORKBENCH.generic.seedContext))
  const [contextFile, setContextFile] = useState(null)
  const [contextResult, setContextResult] = useState(null)
  const [contextRunning, setContextRunning] = useState(false)
  const [contextError, setContextError] = useState('')

  const selectedParse = parseFromEntry(selected)
  const selectedRecording = recordingFromEntry(selected)
  const selectedRequestId = requestIdFromEntry(selected)
  const workflowConfig = WORKFLOWS[workflow] || WORKFLOWS.timer
  const apiBase = workflowConfig.apiBase
  const contextConfig = CONTEXT_WORKBENCH[contextType] || CONTEXT_WORKBENCH.generic
  const selectedContextOption = contextConfig.options.find(option => option.id === contextOption) || contextConfig.options[0]

  const historyParams = useMemo(() => {
    const params = new URLSearchParams()
    params.set('limit', '100')
    if (siteId) params.set('siteId', siteId)
    if (entityType) params.set('entityType', entityType)
    if (entityId) params.set('entityId', entityId)
    if (status) params.set('status', status)
    if (kind) params.set('kind', kind)
    if (needsHumanReview) params.set('needsHumanReview', needsHumanReview)
    return params.toString()
  }, [siteId, entityType, entityId, status, kind, needsHumanReview])

  const baseFields = useCallback(() => ({
    backend,
    model,
    siteId,
    entityType,
    entityId
  }), [backend, model, siteId, entityType, entityId])

  const selectContextType = (nextContext) => {
    const nextConfig = CONTEXT_WORKBENCH[nextContext] || CONTEXT_WORKBENCH.generic
    const nextOption = nextConfig.options[0]
    setContextType(nextContext)
    setContextOption(nextOption.id)
    setContextTranscript(nextOption.en)
    setContextDataText(formatJson(nextConfig.seedContext))
    setContextFile(null)
    setContextResult(null)
    setContextError('')
  }

  const selectContextOption = (nextOptionId) => {
    const nextOption = contextConfig.options.find(option => option.id === nextOptionId) || contextConfig.options[0]
    setContextOption(nextOption.id)
    setContextTranscript(nextOption[contextLanguage === 'es' ? 'es' : 'en'])
    setContextResult(null)
    setContextError('')
  }

  const handleRunContext = async () => {
    if (!contextFile && !contextTranscript.trim()) {
      setContextError('Add a transcript or choose an audio file first.')
      return
    }
    let parsedContextData
    try {
      parsedContextData = parseContextData(contextDataText)
    } catch (error) {
      setContextError(error.message)
      return
    }

    setContextRunning(true)
    setContextError('')
    try {
      const form = new FormData()
      if (contextFile) form.append('file', contextFile)
      if (contextTranscript.trim()) form.append('transcript', contextTranscript.trim())
      form.append('language', contextLanguage)
      form.append('contextData', JSON.stringify(parsedContextData))
      Object.entries(parsedContextData).forEach(([key, value]) => {
        if (value != null && value !== '' && ['string', 'number', 'boolean'].includes(typeof value)) form.append(key, String(value))
      })

      if (contextConfig.processor === 'generic') {
        form.append('context', contextType)
        form.append('optionHint', selectedContextOption.id)
      } else if (contextConfig.processor === 'shop') {
        form.append('intentHint', selectedContextOption.id)
      } else if (contextConfig.processor === 'irrigation') {
        if (selectedContextOption.id === 'inspection') {
          form.append('requestedFields', selectedContextOption.expectedFields.join(','))
          form.append('entityType', 'controller')
        }
        if (selectedContextOption.id === 'timer_program' || selectedContextOption.id === 'station_ohms') form.append('entityType', 'controller')
      }

      const response = await axios.post(`${props.url}${selectedContextOption.endpoint || contextConfig.endpoint}`, form)
      setContextResult(response.data)
    } catch (err) {
      setContextError(err?.response?.data?.details || err?.response?.data?.error || err.message || 'Context parse failed.')
      setContextResult(err?.response?.data || null)
    } finally {
      setContextRunning(false)
    }
  }

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true)
    setError('')
    try {
      const response = await axios.get(`${props.url}${apiBase}/history?${historyParams}`)
      const rows = response?.data?.data || []
      setHistory(rows)
      setSelected(current => current || rows[0] || null)
    } catch (err) {
      setError(err?.response?.data?.details || err.message || 'Failed to load voice audit history.')
    } finally {
      setLoadingHistory(false)
    }
  }, [props.url, apiBase, historyParams])

  useEffect(() => {
    setSelected(null)
    setHistory([])
    loadHistory()
  }, [workflow, loadHistory])

  useEffect(() => {
    const saved = feedbackFromEntry(selected)
    setFeedbackDraft({
      ...emptyFeedback(),
      ...(saved || {}),
      missed: saved?.missed || {},
      wrong: saved?.wrong || {},
      reviewerNote: saved?.reviewerNote || ''
    })
  }, [selected])

  const handleRunUrl = async () => {
    if (!workflowConfig.supportsUrl) {
      setError(`${workflowConfig.label} does not support URL parsing yet. Use file upload or pasted transcript.`)
      return
    }
    if (!recordingUrl.trim()) {
      setError('Paste a recording URL first.')
      return
    }
    setRunning(true)
    setError('')
    try {
      const response = await axios.post(`${props.url}${apiBase}/parse-url`, {
        url: recordingUrl.trim(),
        ...baseFields()
      })
      const entry = {
        requestId: response.data?.requestId || response.data?.data?.requestId || null,
        createdAt: new Date().toISOString(),
        kind: 'url',
        label: recordingUrl.trim(),
        payload: response.data,
        status: response.data?.data?.parse?.needsHumanReview ? 'needs_review' : 'parsed'
      }
      setSelected(entry)
      setActiveTab(0)
      await loadHistory()
    } catch (err) {
      setError(err?.response?.data?.details || err.message || 'URL parse failed.')
      await loadHistory()
    } finally {
      setRunning(false)
    }
  }

  const handleRunTranscript = async () => {
    if (!transcript.trim()) {
      setError('Paste transcript text first.')
      return
    }
    setRunning(true)
    setError('')
    try {
      const form = new FormData()
      form.append('transcript', transcript)
      Object.entries(baseFields()).forEach(([key, value]) => {
        if (value) form.append(key, value)
      })
      const response = await axios.post(`${props.url}${apiBase}/parse`, form)
      const entry = {
        requestId: response.data?.requestId || response.data?.data?.requestId || null,
        createdAt: new Date().toISOString(),
        kind: 'transcript',
        label: 'pasted transcript',
        payload: response.data,
        status: response.data?.data?.parse?.needsHumanReview ? 'needs_review' : 'parsed'
      }
      setSelected(entry)
      setActiveTab(0)
      await loadHistory()
    } catch (err) {
      setError(err?.response?.data?.details || err.message || 'Transcript parse failed.')
      await loadHistory()
    } finally {
      setRunning(false)
    }
  }

  const handleRunFile = async () => {
    if (!file) {
      setError('Choose an audio file first.')
      return
    }
    setRunning(true)
    setError('')
    try {
      const form = new FormData()
      form.append('file', file)
      Object.entries(baseFields()).forEach(([key, value]) => {
        if (value) form.append(key, value)
      })
      const response = await axios.post(`${props.url}${apiBase}/parse`, form)
      const entry = {
        requestId: response.data?.requestId || response.data?.data?.requestId || null,
        createdAt: new Date().toISOString(),
        kind: 'file',
        label: file.name,
        payload: response.data,
        status: response.data?.data?.parse?.needsHumanReview ? 'needs_review' : 'parsed'
      }
      setSelected(entry)
      setActiveTab(0)
      await loadHistory()
    } catch (err) {
      setError(err?.response?.data?.details || err.message || 'File parse failed.')
      await loadHistory()
    } finally {
      setRunning(false)
    }
  }

  const applyRecordingMetadataToFilters = () => {
    if (selectedRecording.siteId) setSiteId(selectedRecording.siteId)
    if (selectedRecording.entityType) setEntityType(selectedRecording.entityType)
    if (selectedRecording.entityId) setEntityId(selectedRecording.entityId)
  }

  const toggleFeedback = (bucket, key) => {
    setFeedbackDraft(current => ({
      ...current,
      [bucket]: {
        ...(current[bucket] || {}),
        [key]: !current[bucket]?.[key]
      }
    }))
  }

  const handleSaveFeedback = async () => {
    if (workflow !== 'arbor') {
      setError('Feedback saving is currently wired for Arbor voice runs.')
      return
    }
    if (!selectedRequestId) {
      setError('This run does not have a request id yet. Refresh history and select the saved row.')
      return
    }
    setSavingFeedback(true)
    setError('')
    try {
      const response = await axios.post(`${props.url}${apiBase}/${selectedRequestId}/feedback`, feedbackDraft)
      const humanFeedback = response?.data?.data?.humanFeedback || feedbackDraft
      setSelected(current => current
        ? {
            ...current,
            humanFeedback,
            payload: current.payload
              ? {
                  ...current.payload,
                  data: {
                    ...(current.payload.data || {}),
                    humanFeedback
                  }
                }
              : current.payload
          }
        : current)
      setHistory(rows => rows.map(row => {
        const rowId = requestIdFromEntry(row)
        return rowId === selectedRequestId ? { ...row, humanFeedback } : row
      }))
    } catch (err) {
      setError(err?.response?.data?.details || err.message || 'Feedback save failed.')
    } finally {
      setSavingFeedback(false)
    }
  }

  const renderContextWorkbench = () => {
    const resultData = contextResultData(contextResult)
    const parsedResult = contextParseData(contextResult)
    const resultStatus = contextResult
      ? (parsedResult.needsHumanReview ? 'Needs review' : contextResult.success === false ? 'Failed' : 'Parsed')
      : 'No test run'
    return (
      <Paper className={classes.contextWorkbench}>
        <div className={classes.contextHeader}>
          <Box>
            <Typography variant="h5">Context Workbench</Typography>
            <Typography variant="body2" color="textSecondary">
              Exercise the nine launch contexts with the fields, actions, and bilingual phrasing we expect from the PWA and mobile entry points.
            </Typography>
          </Box>
          <Box display="flex" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Chip label={`${contextConfig.processor} processor`} color="primary" variant="outlined" />
            <Chip label={resultStatus} color={resultStatus === 'Needs review' ? 'secondary' : 'default'} />
          </Box>
        </div>

        <div className={classes.contextPills}>
          {CONTEXT_ORDER.map(contextId => {
            const config = CONTEXT_WORKBENCH[contextId]
            return (
              <Button
                key={contextId}
                size="small"
                variant={contextId === contextType ? 'contained' : 'outlined'}
                color={contextId === contextType ? 'primary' : 'default'}
                onClick={() => selectContextType(contextId)}
              >
                {config.label}
              </Button>
            )
          })}
        </div>

        <Typography variant="body2" color="textSecondary" gutterBottom>{contextConfig.description}</Typography>
        <div className={classes.contextExpected} style={{ marginBottom: 8 }}>
          <Typography variant="caption" color="textSecondary">Linked context:</Typography>
          {contextConfig.contextFields.map(field => <Chip key={field} size="small" variant="outlined" label={field} />)}
        </div>
        <Typography variant="caption" color="textSecondary">
          Endpoint: {selectedContextOption.endpoint || contextConfig.endpoint}
        </Typography>

        <div className={classes.contextOptions} style={{ marginTop: 12 }}>
          {contextConfig.options.map(option => (
            <Button
              key={option.id}
              className={`${classes.contextOption} ${option.id === selectedContextOption.id ? classes.selectedContextOption : ''}`}
              onClick={() => selectContextOption(option.id)}
            >
              <Box width="100%">
                <Typography variant="subtitle2">{option.label}</Typography>
                <div className={classes.contextExpected}>
                  {option.expectedFields.map(field => <Chip key={field} size="small" label={field} />)}
                </div>
              </Box>
            </Button>
          ))}
        </div>

        <div className={classes.contextWorkbenchGrid}>
          <Box>
            <Box display="flex" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <Button size="small" variant={contextLanguage === 'en' ? 'contained' : 'outlined'} onClick={() => { setContextLanguage('en'); setContextTranscript(selectedContextOption.en) }}>English example</Button>
              <Button size="small" variant={contextLanguage === 'es' ? 'contained' : 'outlined'} onClick={() => { setContextLanguage('es'); setContextTranscript(selectedContextOption.es) }}>Ejemplo en español</Button>
              <Select
                value={contextLanguage}
                onChange={(event) => {
                  const nextLanguage = event.target.value
                  setContextLanguage(nextLanguage)
                  if (nextLanguage !== 'auto') setContextTranscript(selectedContextOption[nextLanguage] || selectedContextOption.en)
                }}
                variant="outlined"
                displayEmpty
                style={{ minWidth: 125 }}
              >
                <MenuItem value="auto">Auto language</MenuItem>
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="es">Español</MenuItem>
              </Select>
            </Box>
            <TextField
              label="Transcript / voice test phrase"
              value={contextTranscript}
              onChange={(event) => setContextTranscript(event.target.value)}
              multiline
              rows={6}
              variant="outlined"
              fullWidth
              placeholder={selectedContextOption.en}
            />
            <input
              id="context-workbench-file"
              className={classes.hiddenInput}
              type="file"
              accept=".m4a,.mp3,.wav,.webm,.ogg,audio/*"
              onChange={(event) => setContextFile(event.target.files?.[0] || null)}
            />
            <Box mt={1} display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap' }}>
              <label htmlFor="context-workbench-file">
                <Button component="span" size="small" variant="outlined" startIcon={<CloudUploadIcon />}>Choose audio</Button>
              </label>
              {contextFile && <Chip size="small" label={contextFile.name} onDelete={() => setContextFile(null)} />}
            </Box>
          </Box>
          <Box>
            <TextField
              label="Entry-point context JSON"
              value={contextDataText}
              onChange={(event) => setContextDataText(event.target.value)}
              multiline
              rows={10}
              variant="outlined"
              fullWidth
              helperText={`Expected keys: ${contextConfig.contextFields.join(', ')}`}
            />
            <Box mt={1} display="flex" style={{ gap: 8, flexWrap: 'wrap' }}>
              <Button variant="contained" color="primary" startIcon={<DescriptionIcon />} onClick={handleRunContext} disabled={contextRunning || (!contextFile && !contextTranscript.trim())}>
                {contextRunning ? 'Parsing context...' : 'Parse Context'}
              </Button>
              <Button variant="text" onClick={() => { setContextResult(null); setContextError(''); setContextDataText(formatJson(contextConfig.seedContext)) }}>Reset test</Button>
            </Box>
          </Box>
        </div>
        {contextError && <Box mt={1}><Chip label={contextError} color="secondary" /></Box>}
        {contextResult && (
          <Box mt={2}>
            <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <Typography variant="subtitle1">Latest context response</Typography>
              {parsedResult.confidence != null && <Chip size="small" label={`confidence ${Number(parsedResult.confidence).toFixed(2)}`} />}
              {Array.isArray(parsedResult.missingRequiredFields) && parsedResult.missingRequiredFields.length > 0 && <Chip size="small" color="secondary" label={`missing ${parsedResult.missingRequiredFields.join(', ')}`} />}
              {Array.isArray(parsedResult.warnings) && parsedResult.warnings.length > 0 && <Chip size="small" color="secondary" label={`${parsedResult.warnings.length} warning(s)`} />}
            </Box>
            <pre className={classes.contextResult}>{formatJson(resultData)}</pre>
          </Box>
        )}
      </Paper>
    )
  }

  const renderTreeCards = () => {
    const structuredData = structuredFromEntry(selected)
    const trees = Array.isArray(structuredData.trees) ? structuredData.trees : []
    if (!selected) {
      return (
        <div className={classes.cardsOutput}>
          <Typography variant="body2" color="textSecondary">Select a run from history or run a new Arbor test.</Typography>
        </div>
      )
    }
    if (!trees.length) {
      return (
        <div className={classes.cardsOutput}>
          <Typography variant="body2" color="textSecondary">No tree records were found in this parser output.</Typography>
        </div>
      )
    }
    return (
      <div className={classes.cardsOutput}>
        {workflow === 'arbor' && (
          <div className={classes.feedbackPanel}>
            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 8, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="subtitle2">Parser Feedback</Typography>
                <Typography variant="caption" color="textSecondary">
                  Saved to the audit row for tuning, evals, and future training exports.
                </Typography>
              </Box>
              <Button
                size="small"
                variant={feedbackDraft.accepted ? 'contained' : 'outlined'}
                color={feedbackDraft.accepted ? 'primary' : 'default'}
                onClick={() => setFeedbackDraft(current => ({ ...current, accepted: !current.accepted }))}
              >
                {feedbackDraft.accepted ? 'Accepted' : 'Mark Accepted'}
              </Button>
            </Box>
            <div className={classes.feedbackRows}>
              <div className={classes.feedbackRow}>
                <Typography variant="caption" color="textSecondary">Missed</Typography>
                <Box display="flex" style={{ gap: 6, flexWrap: 'wrap' }}>
                  {FEEDBACK_CATEGORIES.map(([key, label]) => (
                    <Button
                      key={`missed-${key}`}
                      size="small"
                      variant={feedbackDraft.missed?.[key] ? 'contained' : 'outlined'}
                      color={feedbackDraft.missed?.[key] ? 'secondary' : 'default'}
                      onClick={() => toggleFeedback('missed', key)}
                    >
                      {label}
                    </Button>
                  ))}
                </Box>
              </div>
              <div className={classes.feedbackRow}>
                <Typography variant="caption" color="textSecondary">Wrong</Typography>
                <Box display="flex" style={{ gap: 6, flexWrap: 'wrap' }}>
                  {FEEDBACK_CATEGORIES.map(([key, label]) => (
                    <Button
                      key={`wrong-${key}`}
                      size="small"
                      variant={feedbackDraft.wrong?.[key] ? 'contained' : 'outlined'}
                      color={feedbackDraft.wrong?.[key] ? 'secondary' : 'default'}
                      onClick={() => toggleFeedback('wrong', key)}
                    >
                      {label}
                    </Button>
                  ))}
                </Box>
              </div>
            </div>
            <TextField
              label="Reviewer note"
              value={feedbackDraft.reviewerNote || ''}
              onChange={(event) => setFeedbackDraft(current => ({ ...current, reviewerNote: event.target.value }))}
              variant="outlined"
              multiline
              rows={2}
              fullWidth
              placeholder="What did the parser miss or get wrong?"
            />
            <Box mt={1} display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleSaveFeedback}
                disabled={savingFeedback || !selectedRequestId}
              >
                {savingFeedback ? 'Saving...' : 'Save Feedback'}
              </Button>
              {!selectedRequestId && (
                <Typography variant="caption" color="textSecondary">
                  Refresh history to save feedback for this run.
                </Typography>
              )}
              {feedbackFromEntry(selected)?.updatedAt && (
                <Typography variant="caption" color="textSecondary">
                  Last saved {formatDate(feedbackFromEntry(selected).updatedAt)}
                </Typography>
              )}
            </Box>
          </div>
        )}
        <div className={classes.treeCards}>
          {trees.map((tree, index) => (
            <div key={`${tree.treeNumber || index}-${tree.heardAs || index}`} className={classes.treeCard}>
              <div className={classes.treeCardHeader}>
                <Box>
                  <Typography variant="h6">Tree {tree.treeNumber || index + 1}</Typography>
                  {(tree.groupLabel || tree.groupMemberNumber) && (
                    <Typography variant="caption" color="textSecondary">
                      {valueText(tree.groupLabel)}
                      {tree.groupMemberNumber != null ? ` · group #${tree.groupMemberNumber}` : ''}
                    </Typography>
                  )}
                </Box>
                <Box display="flex" style={{ gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {tree.serviceType && <Chip size="small" label={tree.serviceType} color="primary" />}
                  {tree.inferredFromGroup && <Chip size="small" label="inferred group item" />}
                  {Array.isArray(tree.reviewFlags) && tree.reviewFlags.map(flag => (
                    <Chip key={flag} size="small" label={flag} color="secondary" />
                  ))}
                </Box>
              </div>

              <div className={classes.cardFields}>
                <Box><span className={classes.fieldLabel}>Species</span>{valueText(tree.species)}</Box>
                <Box><span className={classes.fieldLabel}>Height</span>{tree.totalHeightFt != null ? `${tree.totalHeightFt} ft` : '-'}</Box>
                <Box><span className={classes.fieldLabel}>Canopy</span>{tree.canopyWidthFt != null ? `${tree.canopyWidthFt} ft` : '-'}</Box>
                <Box><span className={classes.fieldLabel}>Canopy Base</span>{tree.canopyBaseFt != null ? `${tree.canopyBaseFt} ft` : '-'}</Box>
                <Box><span className={classes.fieldLabel}>DBH</span>{tree.dbhIn != null ? `${tree.dbhIn} in` : '-'}</Box>
                <Box><span className={classes.fieldLabel}>Girth</span>{tree.trunkCircumferenceFt != null ? `${tree.trunkCircumferenceFt} ft` : '-'}</Box>
                <Box><span className={classes.fieldLabel}>Density</span>{valueText(tree.canopyDensity)}</Box>
                <Box><span className={classes.fieldLabel}>Drop Zone</span>{valueText([tree.dropZone, tree.dropZoneDirection].filter(Boolean))}</Box>
              </div>

              <Box display="flex" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {valueText(tree.objectives) !== '-' && <Chip size="small" label={`work: ${valueText(tree.objectives)}`} />}
                {valueText(tree.location) !== '-' && <Chip size="small" label={`location: ${valueText(tree.location)}`} />}
                {valueText(tree.utilityLines) !== '-' && <Chip size="small" label={`utilities: ${valueText(tree.utilityLines)}`} />}
                {valueText(tree.obstacles) !== '-' && <Chip size="small" label={`obstacles: ${valueText(tree.obstacles)}`} />}
                {valueText(tree.access) !== '-' && <Chip size="small" label={`access: ${valueText(tree.access)}`} />}
                {valueText(tree.disposal) !== '-' && <Chip size="small" label={`disposal: ${valueText(tree.disposal)}`} />}
                {valueText(tree.equipment) !== '-' && <Chip size="small" label={`equipment: ${valueText(tree.equipment)}`} />}
              </Box>

              {(valueText(tree.futurePlans) !== '-' || valueText(tree.internalNotes) !== '-' || valueText(tree.oneOffReasons) !== '-') && (
                <Box mb={1}>
                  {valueText(tree.futurePlans) !== '-' && <Typography variant="body2"><strong>Future:</strong> {valueText(tree.futurePlans)}</Typography>}
                  {valueText(tree.internalNotes) !== '-' && <Typography variant="body2"><strong>Internal:</strong> {valueText(tree.internalNotes)}</Typography>}
                  {valueText(tree.oneOffReasons) !== '-' && <Typography variant="body2"><strong>One-off:</strong> {valueText(tree.oneOffReasons)}</Typography>}
                </Box>
              )}

              {tree.heardAs && (
                <Typography variant="caption" color="textSecondary">{tree.heardAs}</Typography>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <Button component={RouterLink} to="/hmiirrigationdashboard" startIcon={<ArrowBackIcon />} variant="outlined">
            Dashboard
          </Button>
          <Box>
            <Typography variant="h4">Voice Audit</Typography>
            <Typography variant="body2" color="textSecondary">
              Review mobile uploads, rerun recordings, and inspect parser output across voice workflows.
            </Typography>
          </Box>
        </div>
        <Box display="flex" style={{ gap: 8, flexWrap: 'wrap' }}>
          <Button
            startIcon={<RefreshIcon />}
            variant="outlined"
            onClick={loadHistory}
            disabled={loadingHistory}
          >
            Refresh
          </Button>
          <Button
            startIcon={<HistoryIcon />}
            variant="outlined"
            href={`${props.url}/api/mobileapp/irrigation/timer-voice/harness`}
            target="_blank"
          >
            Raw Harness
          </Button>
        </Box>
      </div>

      {error && (
        <Box mb={2}>
          <Chip label={error} color="secondary" />
        </Box>
      )}

      {renderContextWorkbench()}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper className={classes.panel}>
            <Typography className={classes.sectionTitle}>Run Test</Typography>
            <Box className={classes.field}>
              <InputLabel>Workflow</InputLabel>
              <Select
                value={workflow}
                onChange={(event) => {
                  const nextWorkflow = event.target.value
                  const nextConfig = WORKFLOWS[nextWorkflow] || WORKFLOWS.timer
                  const previousDefaults = Object.values(WORKFLOWS).map(item => item.defaultEntityType)
                  setWorkflow(nextWorkflow)
                  setError('')
                  setSelected(null)
                  if (!entityType || previousDefaults.includes(entityType)) {
                    setEntityType(nextConfig.defaultEntityType)
                  }
                  if (!nextConfig.supportsUrl && kind === 'url') {
                    setKind('')
                  }
                }}
                fullWidth
              >
                {Object.entries(WORKFLOWS).map(([key, config]) => (
                  <MenuItem key={key} value={key}>{config.label}</MenuItem>
                ))}
              </Select>
            </Box>
            <TextField
              className={classes.field}
              label="Recording URL"
              value={recordingUrl}
              onChange={(event) => setRecordingUrl(event.target.value)}
              multiline
              rows={3}
              variant="outlined"
              fullWidth
              placeholder="https://dflspaces.sfo2.digitaloceanspaces.com/..."
              disabled={!workflowConfig.supportsUrl}
              helperText={workflowConfig.supportsUrl ? '' : 'URL parsing is not enabled for this workflow yet.'}
            />
            <input
              id="voice-audit-file"
              className={classes.hiddenInput}
              type="file"
              accept=".m4a,.mp3,.wav,audio/*"
              onChange={(event) => setFile(event.target.files?.[0] || null)}
            />
            <label htmlFor="voice-audit-file">
              <Button component="span" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth>
                Choose Audio File
              </Button>
            </label>
            {file && (
              <Typography variant="caption" className={classes.fileName} display="block">
                {file.name}
              </Typography>
            )}
            <TextField
              className={classes.field}
              label="Transcript"
              value={transcript}
              onChange={(event) => setTranscript(event.target.value)}
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              style={{ marginTop: 12 }}
              placeholder={workflowConfig.transcriptPlaceholder}
            />
            {Array.isArray(workflowConfig.examples) && workflowConfig.examples.length > 0 && (
              <div className={classes.examples}>
                {workflowConfig.examples.map(example => (
                  <Button
                    key={example.label}
                    size="small"
                    variant="outlined"
                    onClick={() => setTranscript(example.transcript)}
                  >
                    {example.label}
                  </Button>
                ))}
              </div>
            )}

            <div className={classes.controlsGrid}>
              <TextField label="Backend" value={backend} onChange={(event) => setBackend(event.target.value)} variant="outlined" placeholder="server default" />
              <TextField label="Model" value={model} onChange={(event) => setModel(event.target.value)} variant="outlined" placeholder="base.en" />
              <TextField label="Site ID" value={siteId} onChange={(event) => setSiteId(event.target.value)} variant="outlined" />
              <TextField label="Entity Type" value={entityType} onChange={(event) => setEntityType(event.target.value)} variant="outlined" placeholder={workflowConfig.defaultEntityType} />
              <TextField label="Entity ID" value={entityId} onChange={(event) => setEntityId(event.target.value)} variant="outlined" />
            </div>

            <div className={classes.actions}>
              <Button variant="contained" color="primary" startIcon={<LinkIcon />} onClick={handleRunUrl} disabled={running || !workflowConfig.supportsUrl}>
                Parse URL
              </Button>
              <Button variant="contained" color="primary" startIcon={<CloudUploadIcon />} onClick={handleRunFile} disabled={running}>
                Parse File
              </Button>
              <Button variant="outlined" color="primary" startIcon={<DescriptionIcon />} onClick={handleRunTranscript} disabled={running}>
                Parse Transcript
              </Button>
            </div>
          </Paper>

          <Box mt={2}>
            <Paper className={classes.panel}>
              <Typography className={classes.sectionTitle}>History Filters</Typography>
              <div className={classes.controlsGrid}>
                <Box>
                  <InputLabel>Status</InputLabel>
                  <Select value={status} onChange={(event) => setStatus(event.target.value)} fullWidth>
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="parsed">Parsed</MenuItem>
                    <MenuItem value="needs_review">Needs review</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                  </Select>
                </Box>
                <Box>
                  <InputLabel>Kind</InputLabel>
                  <Select value={kind} onChange={(event) => setKind(event.target.value)} fullWidth>
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="file">File</MenuItem>
                    <MenuItem value="url">URL</MenuItem>
                    <MenuItem value="transcript">Transcript</MenuItem>
                  </Select>
                </Box>
                <Box>
                  <InputLabel>Review</InputLabel>
                  <Select value={needsHumanReview} onChange={(event) => setNeedsHumanReview(event.target.value)} fullWidth>
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="true">Needs review</MenuItem>
                    <MenuItem value="false">No review</MenuItem>
                  </Select>
                </Box>
              </div>
            </Paper>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper className={classes.panel}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography className={classes.sectionTitle}>Permanent History</Typography>
              {loadingHistory && <CircularProgress size={20} />}
            </Box>
            <div className={classes.historyList}>
              {history.length === 0 && !loadingHistory ? (
                <Typography variant="body2" color="textSecondary">No audit rows found.</Typography>
              ) : history.map((entry) => (
                <Button
                  key={entry.requestId || `${entry.createdAt}-${entry.label}`}
                  className={`${classes.historyItem} ${selected?.requestId === entry.requestId ? classes.selectedHistoryItem : ''}`}
                  onClick={() => {
                    setSelected(entry)
                    setActiveTab(0)
                  }}
                >
                  <Box width="100%">
                    <Box display="flex" justifyContent="space-between" style={{ gap: 8 }}>
                      <Typography variant="subtitle2">{getDisplayLabel(entry)}</Typography>
                      <Chip size="small" label={entry.status || 'unknown'} color={statusColor(entry.status)} />
                    </Box>
                    <Typography variant="caption" color="textSecondary">{formatDate(entry.createdAt)}</Typography>
                    <Typography variant="body2" color="textSecondary">{getSnippet(entry)}</Typography>
                    <div className={classes.itemMeta}>
                      {entry.kind && <Chip size="small" label={entry.kind} />}
                      {entry.siteId && <Chip size="small" label={`site ${entry.siteId}`} />}
                      {entry.confidence != null && <Chip size="small" label={`confidence ${Number(entry.confidence).toFixed(2)}`} />}
                      {entry.processingMs != null && <Chip size="small" label={`${entry.processingMs}ms`} />}
                    </div>
                  </Box>
                </Button>
              ))}
            </div>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper>
            <div className={classes.statusRow}>
              <Chip label={selected?.status || 'No run selected'} color={statusColor(selected?.status)} />
              {selectedParse.confidence != null && <Chip label={`Confidence ${Number(selectedParse.confidence).toFixed(2)}`} />}
              {selectedParse.needsHumanReview && <Chip label="Needs review" color="secondary" />}
              {selectedRecording.sourceUrl && (
                <Chip
                  label="Source URL"
                  component={Link}
                  clickable
                  href={selectedRecording.sourceUrl}
                  target="_blank"
                />
              )}
              {(selectedRecording.siteId || selectedRecording.entityId) && (
                <Button size="small" onClick={applyRecordingMetadataToFilters}>Use Metadata Filters</Button>
              )}
            </div>
            <Tabs
              value={activeTab}
              onChange={(event, value) => setActiveTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              className={classes.tabs}
            >
              {TABS.map(tab => <Tab key={tab} label={tab} />)}
            </Tabs>
            {TABS[activeTab] === 'Tree Cards'
              ? renderTreeCards()
              : <pre className={classes.output}>{outputForTab(TABS[activeTab], selected)}</pre>}
          </Paper>
        </Grid>
      </Grid>

      <Box mt={2}>
        <Divider />
        <Typography variant="caption" color="textSecondary">
          Audit rows are stored in Mongo collection irrigation_voice_audits. The raw server harness is still available for quick isolated checks.
        </Typography>
      </Box>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url
})

export default connect(mapStateToProps)(IrrigationVoiceAuditPage)
